import React, { useState } from "react";
import { Box, Typography, Button, Tooltip, styled } from "@mui/material";
import type { Pathway } from "../../types";
import { useIsTruncated } from "../../hooks/useIsTruncated";
import { WhatCanIDoDialog } from "./WhatCanIDoDialog";
import { PathwayViewMoreDialog } from "./PathwayViewMoreDialog";

interface PathwaySectionProps {
  pathways?: Pathway[];
  pathwayTaken?: boolean;
  whatCanIDo?: unknown;
  onPathwayClick?: (pathwayId: string) => void;
}

const Container = styled(Box, {
  label: "PathwaySectionContainer",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  width: 176,
}));

const Title = styled(Typography, {
  label: "PathwaySectionTitle",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
  padding: "0 8px",
  whiteSpace: "nowrap",
}));

const PathwayItem = styled(Box, {
  label: "PathwayItem",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  width: "100%",
  cursor: "pointer",
}));

const PathwayImage = styled("img", {
  label: "PathwayImage",
})(() => ({
  width: 64,
  height: 64,
  borderRadius: 16,
  objectFit: "cover",
  flexShrink: 0,
}));

const PathwayName = styled(Typography, {
  label: "PathwayName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  color: "#ffffff",
  lineHeight: "22px",
  flex: 1,
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
}));

const ViewMore = styled(Typography, {
  label: "PathwayViewMore",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  color: "#ffffff",
  lineHeight: "18px",
  cursor: "pointer",
}));

const NoPathwayCard = styled(Box, {
  label: "NoPathwayCard",
})(() => ({
  width: 160,
  height: 160,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  boxShadow:
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
}));

const NoPathwayOverlay = styled(Box, {
  label: "NoPathwayOverlay",
})(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.48)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: 8,
  borderRadius: 16,
}));

const NoPathwayText = styled(Typography, {
  label: "NoPathwayText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  color: "#ffffff",
  lineHeight: "22px",
  textAlign: "center",
}));

const PathwayNameLabel = ({ name }: { name: string }) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>();
  const label = <PathwayName ref={ref}>{name}</PathwayName>;

  if (!isTruncated) return label;

  return (
    <Tooltip title={name} placement="top" enterDelay={500}>
      {label}
    </Tooltip>
  );
};

export const PathwaySection = ({
  pathways,
  pathwayTaken,
  whatCanIDo,
  onPathwayClick,
}: PathwaySectionProps) => {
  const [whatCanIDoOpen, setWhatCanIDoOpen] = useState(false);
  const [viewMoreOpen, setViewMoreOpen] = useState(false);

  if (!pathways || pathways.length === 0) {
    return (
      <Container>
        <Title>Pathway(s)</Title>
        <NoPathwayCard>
          <NoPathwayOverlay>
            <NoPathwayText>No direct CTE Pathway</NoPathwayText>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setWhatCanIDoOpen(true)}
              sx={{
                color: "#ffffff",
                borderColor: "rgba(255, 255, 255, 0.48)",
                textTransform: "none",
                fontFamily: "Proxima Nova, sans-serif",
                fontWeight: 700,
                fontSize: "13px",
                lineHeight: "22px",
                borderRadius: "8px",
                minWidth: 64,
                height: 30,
                px: 1,
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.64)",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              What can I do?
            </Button>
          </NoPathwayOverlay>
        </NoPathwayCard>
        <WhatCanIDoDialog
          open={whatCanIDoOpen}
          onClose={() => setWhatCanIDoOpen(false)}
          richTextContent={whatCanIDo}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Title>{pathways.length === 1 ? "Pathway" : "Pathways"}</Title>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
        }}
      >
        {pathways.slice(0, 2).map((pathway) => (
          <PathwayItem
            key={pathway.id}
            onClick={() => onPathwayClick?.(pathway.id)}
          >
            {pathway.displayImage?.url && (
              <PathwayImage
                src={
                  pathway.displayImage.sizes?.hatCard?.url ||
                  pathway.displayImage.url
                }
                alt={pathway.name}
                sx={
                  pathwayTaken === false
                    ? { filter: "blur(4px)", userSelect: "none" }
                    : undefined
                }
              />
            )}
            {pathwayTaken === false ? (
              <PathwayName sx={{ filter: "blur(4px)", userSelect: "none" }}>
                {pathway.name}
              </PathwayName>
            ) : (
              <PathwayNameLabel name={pathway.name} />
            )}
          </PathwayItem>
        ))}
      </Box>
      {pathways.length > 2 && (
        <ViewMore onClick={() => setViewMoreOpen(true)}>View more</ViewMore>
      )}
      <PathwayViewMoreDialog
        open={viewMoreOpen}
        onClose={() => setViewMoreOpen(false)}
        pathways={pathways}
        onPathwayClick={onPathwayClick}
      />
    </Container>
  );
};
